'use client'

import Image from 'next/image'
import { useTranslations } from 'next-intl'
import {
  LayoutDashboard,
  UtensilsCrossed,
  CalendarCheck,
  Truck,
  Users,
  Palette,
  BarChart3,
  Settings,
  HelpCircle,
  Bell,
  Copy,
  RefreshCw
} from 'lucide-react'
import MockupChrome from './MockupChrome'
import '../../sass/landing/DashboardMockup.scss'

type NavKey = 'dashboard' | 'menu' | 'bookings' | 'delivery' | 'customers' | 'customize' | 'analytics' | 'settings'

interface NavItem {
  key: NavKey
  icon: typeof LayoutDashboard
  active?: boolean
}

const navItems: NavItem[] = [
  { key: 'dashboard', icon: LayoutDashboard, active: true },
  { key: 'menu', icon: UtensilsCrossed },
  { key: 'bookings', icon: CalendarCheck },
  { key: 'delivery', icon: Truck },
  { key: 'customers', icon: Users },
  { key: 'customize', icon: Palette },
  { key: 'analytics', icon: BarChart3 },
  { key: 'settings', icon: Settings }
]

type ReservationKey = 'r1' | 'r2' | 'r3' | 'r4' | 'r5' | 'r6'

interface ReservationRow {
  id: ReservationKey
  party: number
  status: 'ok' | 'cancelled'
}

const reservations: ReservationRow[] = [
  { id: 'r1', party: 4, status: 'ok' },
  { id: 'r2', party: 2, status: 'cancelled' },
  { id: 'r3', party: 6, status: 'ok' },
  { id: 'r4', party: 2, status: 'ok' },
  { id: 'r5', party: 3, status: 'ok' },
  { id: 'r6', party: 4, status: 'ok' }
]

const visitsPath =
  'M0 47 C 0 47, 26 37, 39 32 C 52 27, 65 15, 78 18 C 91 21, 104 47, 117 50 C 130 53, 143 41, 156 36 C 169 31, 182 24, 195 20 C 208 16, 234 14, 234 14 L 234 90 L 0 90 Z'

const visitsLine =
  'M0 47 C 0 47, 26 37, 39 32 C 52 27, 65 15, 78 18 C 91 21, 104 47, 117 50 C 130 53, 143 41, 156 36 C 169 31, 182 24, 195 20 C 208 16, 234 14, 234 14'

const QrBlock = () => {
  const grid = 21
  const cell = 4
  const padding = 1
  const off = padding * cell
  const total = (grid + 2 * padding) * cell

  // Hand-tuned 21x21 pattern that mimics a real QR code's appearance.
  // 1 = black module, 0 = white. Finder patterns and timing are rendered on top.
  const pattern: number[][] = [
    [0, 0, 0, 0, 0, 0, 0, 0, 1, 1, 0, 0, 1, 0, 0, 0, 0, 0, 0, 0, 0],
    [0, 0, 0, 0, 0, 0, 0, 0, 0, 1, 0, 0, 1, 1, 0, 1, 1, 1, 0, 0, 0],
    [0, 0, 0, 0, 0, 0, 0, 0, 1, 0, 0, 1, 0, 1, 0, 1, 0, 0, 1, 0, 0],
    [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 1, 0, 1, 0, 0, 1, 0, 1, 1, 0, 0],
    [0, 0, 0, 0, 0, 0, 0, 0, 1, 1, 0, 1, 0, 0, 0, 1, 0, 0, 1, 0, 0],
    [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 1, 0, 1, 1, 0, 1, 1, 0, 0, 0, 0],
    [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
    [0, 0, 0, 0, 0, 0, 0, 0, 1, 1, 1, 0, 1, 0, 0, 0, 0, 0, 0, 0, 0],
    [0, 0, 0, 0, 0, 0, 0, 0, 0, 1, 0, 0, 1, 1, 0, 0, 0, 1, 0, 1, 0],
    [1, 1, 0, 1, 0, 1, 0, 0, 0, 0, 1, 0, 1, 0, 0, 0, 1, 0, 0, 1, 1],
    [0, 1, 0, 0, 0, 1, 0, 1, 1, 0, 0, 1, 1, 0, 0, 0, 0, 1, 0, 1, 0],
    [1, 0, 1, 0, 1, 0, 0, 0, 1, 1, 0, 0, 0, 1, 0, 1, 1, 0, 0, 0, 0],
    [0, 0, 1, 1, 0, 0, 0, 0, 1, 0, 0, 1, 1, 0, 0, 0, 0, 1, 1, 0, 0],
    [0, 0, 0, 0, 0, 0, 0, 0, 1, 1, 0, 0, 1, 1, 0, 1, 0, 0, 0, 0, 0],
    [0, 0, 0, 0, 0, 0, 0, 0, 1, 0, 1, 1, 0, 0, 0, 0, 1, 0, 0, 1, 0],
    [1, 0, 1, 0, 1, 0, 0, 0, 1, 1, 0, 0, 1, 0, 0, 1, 0, 0, 1, 0, 0],
    [0, 0, 1, 1, 0, 1, 0, 0, 1, 0, 0, 1, 0, 0, 0, 0, 0, 1, 0, 1, 0],
    [1, 1, 0, 0, 0, 0, 0, 0, 0, 0, 0, 1, 1, 0, 0, 0, 0, 0, 1, 0, 1],
    [0, 0, 1, 1, 1, 0, 0, 0, 1, 1, 0, 0, 1, 0, 0, 1, 1, 0, 1, 0, 1],
    [0, 1, 0, 0, 0, 0, 0, 0, 1, 0, 1, 1, 0, 1, 0, 0, 0, 0, 1, 0, 0],
    [0, 0, 0, 0, 0, 0, 0, 0, 0, 1, 1, 0, 1, 0, 0, 0, 0, 0, 0, 0, 0]
  ]

  const isFinder = (x: number, y: number) =>
    (x < 7 && y < 7) ||
    (x >= grid - 7 && y < 7) ||
    (x < 7 && y >= grid - 7)

  const dataCells: Array<{ x: number, y: number }> = []
  for (let y = 0; y < grid; y += 1) {
    for (let x = 0; x < grid; x += 1) {
      if (isFinder(x, y)) continue
      if (x === 6 || y === 6) continue
      if (pattern[y][x] === 1) {
        dataCells.push({ x: off + x * cell, y: off + y * cell })
      }
    }
  }

  const finder = (cx: number, cy: number) => (
    <g transform={`translate(${off + cx * cell} ${off + cy * cell})`}>
      <rect width={7 * cell} height={7 * cell} fill='#000000' />
      <rect x={cell} y={cell} width={5 * cell} height={5 * cell} fill='#ffffff' />
      <rect x={2 * cell} y={2 * cell} width={3 * cell} height={3 * cell} fill='#000000' />
    </g>
  )

  const timingCells: Array<{ x: number, y: number }> = []
  for (let i = 8; i <= 12; i += 1) {
    if (i % 2 === 0) {
      timingCells.push({ x: off + i * cell, y: off + 6 * cell })
      timingCells.push({ x: off + 6 * cell, y: off + i * cell })
    }
  }

  return (
    <svg viewBox={`0 0 ${total} ${total}`} className='dashboard-mockup__qr-svg' aria-hidden='true'>
      <rect width={total} height={total} fill='#ffffff' />
      {dataCells.map((c, i) => (
        <rect key={i} x={c.x} y={c.y} width={cell - 0.15} height={cell - 0.15} fill='#000000' />
      ))}
      {timingCells.map((c, i) => (
        <rect key={i} x={c.x} y={c.y} width={cell - 0.15} height={cell - 0.15} fill='#000000' />
      ))}
      {finder(0, 0)}
      {finder(grid - 7, 0)}
      {finder(0, grid - 7)}
    </svg>
  )
}

const DashboardMockup = () => {
  const t = useTranslations('LandingV2.mockups.dashboard')

  return (
    <div className='dashboard-mockup' aria-label={t('ariaLabel')}>
      <MockupChrome
        icon={<LayoutDashboard size={11} strokeWidth={2.4} />}
        address={t('address')}
      />

      <div className='dashboard-mockup__app'>
        <aside className='dashboard-mockup__sidebar'>
          <div className='dashboard-mockup__brand'>
            <Image
              src='/img/barmanager-logo.svg'
              alt={t('brand')}
              width={120}
              height={22}
              className='dashboard-mockup__brand-text'
            />
          </div>
          <nav className='dashboard-mockup__nav' aria-label={t('navAria')}>
            {navItems.map(({ key, icon: Icon, active }) => (
              <div
                key={key}
                className={`dashboard-mockup__nav-item${active ? ' dashboard-mockup__nav-item--active' : ''}`}
              >
                <Icon size={14} strokeWidth={2.1} />
                <span>{t(`nav.${key}`)}</span>
              </div>
            ))}
          </nav>
        </aside>

        <div className='dashboard-mockup__main'>
          <header className='dashboard-mockup__topbar'>
            <div className='dashboard-mockup__topbar-spacer' />
            <button type='button' tabIndex={-1} className='dashboard-mockup__icon-btn' aria-label={t('helpAria')}>
              <HelpCircle size={14} />
            </button>
            <button type='button' tabIndex={-1} className='dashboard-mockup__icon-btn dashboard-mockup__icon-btn--badge' aria-label={t('notificationsAria')}>
              <Bell size={14} />
            </button>
            <div className='dashboard-mockup__bar-pill'>
              <span className='dashboard-mockup__bar-logo' aria-hidden='true'>R</span>
              <span>{t('myBar')}</span>
            </div>
          </header>

          <div className='dashboard-mockup__content'>
            <section className='dashboard-mockup__hero'>
              <div className='dashboard-mockup__hero-text'>
                <h3>{t('heroTitle')}</h3>
                <p>{t('heroDescription')}</p>
              </div>
              <button type='button' tabIndex={-1} className='dashboard-mockup__btn dashboard-mockup__btn--accent'>
                {t('editMenu')}
              </button>
            </section>

            <section className='dashboard-mockup__preview'>
              <header className='dashboard-mockup__card-head'>
                <h3>{t('previewTitle')}</h3>
                <button type='button' tabIndex={-1} className='dashboard-mockup__btn dashboard-mockup__btn--outline'>
                  {t('viewMenu')}
                </button>
              </header>
              <div className='dashboard-mockup__menu-strip'>
                <div className='dashboard-mockup__menu-card'>
                  <div className='dashboard-mockup__menu-info'>
                    <span className='dashboard-mockup__menu-name'>{t('sampleItem.name')}</span>
                    <span className='dashboard-mockup__menu-desc'>{t('sampleItem.description')}</span>
                  </div>
                  <span className='dashboard-mockup__menu-price'>{t('sampleItem.price')}</span>
                </div>
              </div>
            </section>

            <section className='dashboard-mockup__qr'>
              <header className='dashboard-mockup__card-head'>
                <h3>{t('qrTitle')}</h3>
                <button type='button' tabIndex={-1} className='dashboard-mockup__btn dashboard-mockup__btn--outline'>
                  {t('downloadQr')}
                </button>
              </header>
              <div className='dashboard-mockup__qr-body'>
                <QrBlock />
              </div>
            </section>

            <section className='dashboard-mockup__chart'>
              <header className='dashboard-mockup__card-head'>
                <h3>{t('chartTitle')}</h3>
                <span className='dashboard-mockup__card-meta'>{t('chartPeriod')}</span>
              </header>
              <div className='dashboard-mockup__chart-body'>
                <div className='dashboard-mockup__chart-y'>
                  <span>{t('chartAxis.y200')}</span>
                  <span>{t('chartAxis.y150')}</span>
                  <span>{t('chartAxis.y100')}</span>
                  <span>{t('chartAxis.y50')}</span>
                  <span>{t('chartAxis.y0')}</span>
                </div>
                <svg viewBox='0 0 235 90' className='dashboard-mockup__chart-svg' preserveAspectRatio='none' aria-hidden='true'>
                  <defs>
                    <linearGradient id='chartFill' x1='0' y1='0' x2='0' y2='1'>
                      <stop offset='0%' stopColor='#10b981' stopOpacity='0.25' />
                      <stop offset='100%' stopColor='#10b981' stopOpacity='0' />
                    </linearGradient>
                  </defs>
                  <path d={visitsPath} fill='url(#chartFill)' />
                  <path d={visitsLine} fill='none' stroke='#10b981' strokeWidth='1.5' />
                </svg>
              </div>
            </section>

            <section className='dashboard-mockup__metric'>
              <header className='dashboard-mockup__card-head'>
                <h3>{t('metricTitle')}</h3>
                <span className='dashboard-mockup__card-meta'>{t('metricPeriod')}</span>
              </header>
              <div className='dashboard-mockup__metric-body'>
                <span className='dashboard-mockup__metric-value'>{t('metricValue')}</span>
                <span className='dashboard-mockup__metric-delta dashboard-mockup__metric-delta--up'>{t('metricDelta')}</span>
                <span className='dashboard-mockup__metric-foot'>{t('metricFoot')}</span>
              </div>
            </section>

            <section className='dashboard-mockup__settings'>
              <div className='dashboard-mockup__toggle-row'>
                <span>{t('toggles.noAds')}</span>
                <span className='dashboard-mockup__toggle dashboard-mockup__toggle--on' aria-hidden='true'>
                  <span className='dashboard-mockup__toggle-knob' />
                </span>
              </div>
              <div className='dashboard-mockup__toggle-row'>
                <span>{t('toggles.translations')}</span>
                <span className='dashboard-mockup__toggle dashboard-mockup__toggle--on' aria-hidden='true'>
                  <span className='dashboard-mockup__toggle-knob' />
                </span>
              </div>
            </section>

            <section className='dashboard-mockup__links'>
              <div className='dashboard-mockup__tabs' role='tablist'>
                <button type='button' tabIndex={-1} role='tab' aria-selected='true' className='dashboard-mockup__tab dashboard-mockup__tab--active'>
                  {t('tabs.menu')}
                </button>
                <button type='button' tabIndex={-1} role='tab' aria-selected='false' className='dashboard-mockup__tab'>
                  {t('tabs.bookings')}
                </button>
                <button type='button' tabIndex={-1} role='tab' aria-selected='false' className='dashboard-mockup__tab'>
                  {t('tabs.orders')}
                </button>
              </div>
              <div className='dashboard-mockup__link-row'>
                <span className='dashboard-mockup__link-url'>{t('menuUrl')}</span>
                <button type='button' tabIndex={-1} className='dashboard-mockup__copy-btn' aria-label={t('copyAria')}>
                  <Copy size={11} strokeWidth={2.4} />
                </button>
              </div>
            </section>

            <section className='dashboard-mockup__reservations' aria-label={t('reservationsAria')}>
              <header className='dashboard-mockup__reservations-head'>
                <h3>{t('reservationsTitle')}</h3>
                <span className='dashboard-mockup__refresh' aria-hidden='true'>
                  <RefreshCw size={11} strokeWidth={2.4} />
                </span>
              </header>
              <ul className='dashboard-mockup__reservation-list'>
                {reservations.map((r) => (
                  <li
                    key={r.id}
                    className={`dashboard-mockup__reservation${r.status === 'cancelled' ? ' dashboard-mockup__reservation--cancelled' : ''}`}
                  >
                    <span className='dashboard-mockup__reservation-bar' aria-hidden='true' />
                    <div className='dashboard-mockup__reservation-body'>
                      <span className='dashboard-mockup__reservation-name'>{t(`reservations.${r.id}.name`)}</span>
                      <span className='dashboard-mockup__reservation-foot'>
                        <span className='dashboard-mockup__reservation-party'>{r.party}</span>
                        <span className='dashboard-mockup__reservation-icon' aria-hidden='true'>
                          <Users size={8} strokeWidth={2.6} />
                        </span>
                      </span>
                    </div>
                    <div className='dashboard-mockup__reservation-meta'>
                      <span className='dashboard-mockup__reservation-date'>{t(`reservations.${r.id}.date`)}</span>
                      {r.status === 'cancelled' && (
                        <span className='dashboard-mockup__reservation-status'>{t('cancelledStatus')}</span>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
              <button type='button' tabIndex={-1} className='dashboard-mockup__reservations-link'>
                {t('reservationsLink')}
              </button>
            </section>
          </div>
        </div>
      </div>
    </div>
  )
}

export default DashboardMockup
